import { useMemo, useState } from 'react';
import styled from 'styled-components';

import Button from 'components/shared/Button';
import StandardModal, { Actions, SubHeader } from 'components/shared/StandardModal';
import { simpleDate } from 'lib/date';
import { SyncPatientToEhrAction } from 'models/SyncPatientToEhrAction';
import Workflow from 'models/Workflow';
import { WorkflowValuesInput } from 'services/api/workflows';
import { colors } from 'styles/theme/colors';
import { BodySmall } from 'styles/typography';

import { VALUE_NAMES } from '../constants';
import RevealableSsn from '../RevealableSsn';

import EditableField from './EditableField';
import { MATRIXCARE, optionsForField, syncFieldErrors, syncFields } from './fields';
import { platformLabel } from './SyncPatientBody';

interface Props {
  workflow: Workflow;
  // The step being sent. Read for what the LAST dispatch submitted, so a fix-and-resend does not make
  // the coordinator retype a correction the vendor already rejected the referral over.
  action: SyncPatientToEhrAction;
  // Corrections plus the document selection, both staged on the dispatch. The corrections are NOT
  // written back to the referral: those names are declared inputs of the auto-checks, and writing them
  // back re-ran the denial list and the sex offender check (QA, 2026-08-18).
  onSend: (values: WorkflowValuesInput, outputs: WorkflowValuesInput) => Promise<unknown>;
  onClose: () => void;
  // Reopened to fix a field the vendor rejected — skip the document step and focus the MRN.
  startOnReview?: boolean;
}

// One rhythm for the whole modal: 24px between a step's blocks, and 16px on top of the 24 that
// StyledContent already contributes, so the first block sits 40px clear of the header. Both numbers
// are ReferralModal's, measured off the mock there rather than re-eyeballed here.
//
// The gap belongs to the step container, not to each block: as a plain `block` this stacked the
// buttons flush against whatever copy preceded them, and every new note or warning had to remember
// to bring its own margin. Owning it here means a step added later is spaced correctly by default.
const Form = styled.div<{ $hidden?: boolean }>`
  display: ${({ $hidden }) => ($hidden ? 'none' : 'flex')};
  flex-direction: column;
  gap: 24px;
  padding-top: 16px;
`;

// Lists keep their own tighter rhythm. Their rows already carry vertical padding, so inheriting the
// form's 24px would put a paragraph-sized hole between every file and every field.
const DocList = styled.div`
  display: flex;
  flex-direction: column;
`;

const Fields = styled.div`
  display: flex;
  flex-direction: column;
`;

const DocRow = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 0;
  cursor: pointer;
`;

const DocMeta = styled(BodySmall)`
  color: ${colors.black50};
`;

const SelectAll = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: ${colors.blue100};
  text-decoration: underline;
  align-self: flex-start;
`;

const Note = styled(BodySmall)`
  color: ${colors.black50};
`;

const ErrorSummary = styled(BodySmall)`
  color: ${colors.red100};
`;

// The mock opens the step with an instruction, not a label: it says what to do with the list below
// rather than restating the destination the modal header already names. The form's gap owns the space
// underneath, so SubHeader's own 16px is dropped or it stacks into a hole above the first field.
const Lead = styled(SubHeader)`
  margin-bottom: 0;
  color: ${colors.black100};
`;

/**
 * Review-and-send, before a patient is written into a real EHR (OLIO-2846).
 *
 * Two steps, in the screenshots' order: pick the documents, then review the demographics. There is no
 * stepper component in the repo, so this follows ReferralModal's house pattern — the inactive step is
 * HIDDEN rather than unmounted, so Back restores the selection and any in-progress field edit instead
 * of resetting them.
 *
 * Documents are RECORDED, NOT DELIVERED by this story: the platform stores the manifest and shows it
 * as "not yet sent". Delivery needs cross-account S3 access and a different vendor API entirely.
 */
export default function SyncPatientModal({ workflow, action, onSend, onClose, startOnReview = false }: Props) {
  const platform = workflow.ehrSync.platform;
  const platformName = platformLabel(platform);
  const fields = useMemo(() => syncFields(platform), [platform]);

  const [step, setStep] = useState<'documents' | 'review'>(startOnReview ? 'review' : 'documents');
  const [sending, setSending] = useState(false);

  // What the REFERRAL holds. The diff below is always taken against this, never against what is on
  // screen — that distinction is the whole reason a resend works: a correction inherited from the last
  // attempt still differs from the referral, so it is sent again rather than computing an empty diff
  // and silently reverting to the referral's value.
  const committed = useMemo(
    () => Object.fromEntries(fields.map((field) => [field.name, workflow.valueFor(field.name)?.value ?? ''])),
    [fields, workflow]
  );

  // What the screen opens with: the referral, overlaid with whatever the last dispatch submitted. So a
  // vendor rejection reopens showing the corrected data the coordinator already typed.
  const opening = useMemo(() => ({ ...committed, ...action.submittedDemographics }), [committed, action]);
  const [values, setValues] = useState<Record<string, string>>(opening);

  // One row open at a time, per the mock. Owned here rather than per row because a select or a date
  // only leaves edit mode by being changed: a row opened and left alone stayed open, and opening a
  // second one put two inputs on screen at once.
  const [editingField, setEditingField] = useState<string | null>(null);

  // The packet is already on the workflow — no fetch. Default to everything selected, since the
  // coordinator opened this to send the referral's documents.
  const [selectedDocs, setSelectedDocs] = useState<string[]>(() => workflow.attachments.map((a) => a.id));

  const errors = syncFieldErrors(values, platform);
  const blocked = Object.keys(errors).length > 0;

  const setValue = (name: string, value: string) => setValues((prev) => ({ ...prev, [name]: value }));

  const toggleDoc = (id: string) =>
    setSelectedDocs((prev) => (prev.includes(id) ? prev.filter((docId) => docId !== id) : [...prev, id]));

  const send = async () => {
    setSending(true);
    try {
      // Everything that differs from the REFERRAL, which includes a correction carried over from the
      // last attempt. Diffing against what the screen opened with instead would send nothing on an
      // unedited resend, and the vendor would receive the value the referral holds — the one it just
      // rejected.
      const changed = Object.fromEntries(Object.entries(values).filter(([name, value]) => value !== committed[name]));
      await onSend(changed, { selected_attachment_ids: selectedDocs });
      onClose();
    } catch {
      // Stay OPEN and swallow. The toast already carries the server's own sentence, and closing on a
      // failure is the specific bug this catch exists to prevent: the coordinator would be left
      // looking at a card that had not changed, with no way to tell whether the send had happened.
      // Swallowing rather than rethrowing keeps a failed send from surfacing as an unhandled
      // rejection — this handler IS how the failure is dealt with.
    } finally {
      // Reached on both paths; harmless after a close, and on failure it is what stops the button
      // spinning forever over an error the coordinator needs to act on.
      setSending(false);
    }
  };

  const title = step === 'documents' ? 'Select documents to send' : 'Review patient details';

  return (
    // 560px explicitly: StandardModal defaults to flex-basis 480px with `flex: 0`, so it never grows
    // into its 560 max — and the label/value rows wrap at 480.
    <StandardModal title={title} onCancel={onClose} maxWidth='560px'>
      <Form $hidden={step !== 'documents'} data-testid='sync-step-documents'>
        {workflow.attachments.length === 0 ? (
          <Note data-testid='sync-no-documents'>This referral has no documents to send.</Note>
        ) : (
          <>
            <SelectAll
              type='button'
              onClick={() =>
                setSelectedDocs(
                  selectedDocs.length === workflow.attachments.length ? [] : workflow.attachments.map((a) => a.id)
                )
              }
              data-testid='sync-select-all'>
              {selectedDocs.length === workflow.attachments.length ? 'Select none' : 'Select all'}
            </SelectAll>
            <DocList>
              {workflow.attachments.map((attachment) => (
                <DocRow key={attachment.id}>
                  <input
                    type='checkbox'
                    checked={selectedDocs.includes(attachment.id)}
                    onChange={() => toggleDoc(attachment.id)}
                    data-testid={`sync-doc-${attachment.id}`}
                  />
                  <BodySmall>{attachment.filename ?? 'Document'}</BodySmall>
                  {/* No file size: the workflow payload carries only id/filename/docType. Size is in
                      the manifest the platform records, which is where it is actually reported. */}
                  <DocMeta>{attachment.docType ?? ''}</DocMeta>
                </DocRow>
              ))}
            </DocList>
          </>
        )}
        {/* Load-bearing, not a caveat: a picker with no disclaimer is how somebody concludes the
            documents reached the vendor. */}
        <Note data-testid='sync-docs-not-sent'>
          Selected documents are recorded with this sync but are not sent to {platformName} yet.
        </Note>
        <Actions>
          <Button variant='outline-primary' size='small' onClick={onClose}>
            Cancel
          </Button>
          <Button variant='primary' size='small' onClick={() => setStep('review')} data-testid='sync-next'>
            Next
          </Button>
        </Actions>
      </Form>

      <Form $hidden={step !== 'review'} data-testid='sync-step-review'>
        <Lead>Review the fields below before sending to {platformName}.</Lead>

        <Fields>
          {fields.map((field) => (
            <EditableField
              key={field.name}
              field={field}
              value={values[field.name] ?? ''}
              onChange={(value) => setValue(field.name, value)}
              options={optionsForField(field.name)}
              error={errors[field.name]}
              isEditing={editingField === field.name}
              onStartEdit={() => setEditingField(field.name)}
              onEndEdit={() => setEditingField((current) => (current === field.name ? null : current))}
              // Masked while reading, raw while editing — and NOT masked when it is invalid, because
              // masking a value you are asking someone to fix is the actual bug (the mask is what made a
              // redacted number invisible in the first place).
              // The date reads MM/DD/YYYY here while the stored value stays ISO — display only, so the
              // diff that decides what gets sent is untouched. Falls back to the raw string when it
              // won't parse: an unreadable capture is still what we hold, and blanking it would hide
              // the very thing the coordinator opened this screen to fix.
              renderValue={
                field.name === VALUE_NAMES.PATIENT_SSN && !errors[field.name]
                  ? (value) => <RevealableSsn ssn={value} />
                  : field.type === 'date'
                    ? (value) => <>{simpleDate(value) ?? value}</>
                    : undefined
              }
            />
          ))}
        </Fields>

        {platform === MATRIXCARE && (
          <Note>
            If this facility assigns medical record numbers by hand, turn it on in the patient-sync settings and Olio
            will generate one. There is nothing to enter here.
          </Note>
        )}

        {blocked && (
          <ErrorSummary data-testid='sync-blocked'>
            Correct the highlighted fields before sending to {platformName}.
          </ErrorSummary>
        )}

        <Actions>
          <Button
            variant='outline-primary'
            size='small'
            onClick={() => (startOnReview ? onClose() : setStep('documents'))}
            data-testid='sync-back'>
            {startOnReview ? 'Cancel' : 'Back'}
          </Button>
          <Button
            variant='primary'
            size='small'
            disabled={blocked || sending}
            onClick={send}
            data-testid='sync-confirm-send'>
            {sending ? 'Sending…' : `Send to ${platformName}`}
          </Button>
        </Actions>
      </Form>
    </StandardModal>
  );
}
